import 'bootstrap/dist/css/bootstrap.min.css';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FaPlus } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { StyleFormModal, VariantFormModal } from '../../components/modals/StyleModals';
import MeasureGuideModal from '../../components/modals/MeasureGuideModal';
import SuccessModal from '../../components/modals/SuccessModal';
import { useNotificationModal } from '../../hooks/useNotificationModal';
import StyleTable from '../../components/StyleTable';
import Spinner from '../../components/Spinner';
import { AuthContext } from '../../context/AuthContext';
import '../../styles/Style.css';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export default function Style() {
  const navigate = useNavigate();
  const [styles, setStyles] = useState([]);
  const [mainCategories, setMainCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [selectedMainCategory, setSelectedMainCategory] = useState('');
  const [marketplaceCategories, setMarketplaceCategories] = useState([]);
  const [selectedMarketplaceCategory, setSelectedMarketplaceCategory] = useState('');
  const [marketplaceSubcategories, setMarketplaceSubcategories] = useState([]);
  const [, setSelectedMarketplaceSubcategory] = useState('');
  const [loading, setLoading] = useState(false);
  const { userData } = useContext(AuthContext);
  const company_code = userData?.company_code;
  const {
    showSuccessModal,
    successMessage,
    successTitle,
    showErrorModal,
    errorMessage,
    errorTitle,
    showSuccess,
    showError,
    hideSuccess,
    hideError
  } = useNotificationModal();

  // Company currency state
  const [companyCurrency, setCompanyCurrency] = useState('USD');
  // Fetch company currency
  const fetchCompanyCurrency = useCallback(async () => {
    if (!company_code) return;
    try {
      const response = await axios.get(`${BASE_URL}/api/admin/companies-by-code/${company_code}`);
      const data = response.data;
      if (data.success && data.company && data.company.currency) {
        setCompanyCurrency(data.company.currency);
      }
    } catch (err) {
      // fallback to USD
      setCompanyCurrency('USD');
    }
  }, [company_code]);

  // Modal states
  const [showStyleModal, setShowStyleModal] = useState(false);
  const [editingStyle, setEditingStyle] = useState(null);
  const [showVariantModal, setShowVariantModal] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [variants, setVariants] = useState([]);
  const [isEditingVariant, setIsEditingVariant] = useState(false);
  const [editingVariantId, setEditingVariantId] = useState(null);
  const [showMeasureGuideModal, setShowMeasureGuideModal] = useState(false);

  // company_code is now initialized above

  // Form states updated for multiple images
  const [styleForm, setStyleForm] = useState({
    style_number: '', // Style Number (used as style_number)
    name: '',
    description: '',
    category_id: '',
    main_category_id: '',
    images: [],
    company_code: company_code,
    is_view: 'no' // default to 'no'
  });

  const [variantForm, setVariantForm] = useState({
    color_id: '',
    size_id: '',
    fit_id: '',
    material_id: '',
    unit_price: '',
    sale_price: '',

  });

  // Add these new states
  const [colors, setColors] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [fits, setFits] = useState([]);

  // API Functions
  const fetchStyles = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/api/admin/styles/get-styles`, {
        params: { company_code },
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      const data = response.data;

      if (data.success) {
        setStyles(data.styles);
      } else {
        showError(data.message || 'Failed to fetch products', 'Error');
      }
    } catch (err) {
      console.error('Error fetching products:', err); // Debug log
      showError(err.message || 'Error fetching products', 'Error');
    } finally {
      setLoading(false);
    }
  }, [company_code, showError]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/admin/categories/get-categories?company_code=${company_code}`);
      const data = await response.json();
      if (data.success) {
        const mainCats = data.categories.filter(cat => !cat.parent_id);
        setMainCategories(mainCats);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  }, [company_code]);

  const fetchVariants = useCallback(async (styleNumber) => {
    try {
      const response = await fetch(`${BASE_URL}/api/admin/styles/get-style-variants/${styleNumber}?company_code=${company_code}`);
      const data = await response.json();
      if (data.success) {
        setVariants(data.variants);
      }
    } catch (err) {
      showError(err.message || 'Error fetching variants', 'Error');
    }
  }, [company_code, showError]);

  const fetchColors = useCallback(async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/admin/colors/get-colors?company_code=${company_code}`);
      const data = await response.json();
      if (data.success) {
        setColors(data.colors);
      }
    } catch (err) {
      showError(err.message || 'Error fetching colors', 'Error');
    }
  }, [company_code, showError]);

  const fetchSizes = useCallback(async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/admin/sizes/get-sizes?company_code=${company_code}`);
      const data = await response.json();
      if (data.success) {
        setSizes(data.sizes);
      }
    } catch (err) {
      showError(err.message || 'Error fetching sizes', 'Error');
    }
  }, [company_code, showError]);

  const fetchMaterials = useCallback(async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/admin/materials/get-materials?company_code=${company_code}`);
      const data = await response.json();
      if (data.success) {
        setMaterials(data.materials);
      }
    } catch (err) {
      showError(err.message || 'Error fetching materials', 'Error');
    }
  }, [company_code, showError]);

  const fetchFits = useCallback(async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/admin/fits/get-fits?company_code=${company_code}`);
      const data = await response.json();
      if (data.success) {
        setFits(data.fits);
      }
    } catch (err) {
      showError(err.message || 'Error fetching fits', 'Error');
    }
  }, [company_code, showError]);

  const fetchMarketplaceCategories = useCallback(async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/admin/categories/get_marketplace_categories`);
      const data = await response.json();
      if (data.success) {
        setMarketplaceCategories(data.categories || []);
      }
    } catch (err) {
      console.error('Error fetching marketplace categories:', err);
      showError(err.message || 'Error fetching marketplace categories', 'Error');
    }
  }, [showError]);

  // Fetch all data on component mount
  useEffect(() => {
    fetchStyles();
    fetchCategories();
    fetchColors();
    fetchSizes();
    fetchMaterials();
    fetchFits();
    fetchMarketplaceCategories();
    fetchCompanyCurrency();
  }, [fetchStyles, fetchCategories, fetchColors, fetchSizes, fetchMaterials, fetchFits, fetchMarketplaceCategories, fetchCompanyCurrency]);

  const handleAddStyle = () => {
    setEditingStyle(null);
    setSelectedMainCategory('');
    setSelectedMarketplaceCategory('');
    setSelectedMarketplaceSubcategory('');
    setStyleForm({
      style_number: '',
      name: '',
      description: '',
      category_id: '',
      main_category_id: '',
      marketplace_category_id: '',
      images: [],
      company_code: company_code,
      is_view: 'no'
    });
    setShowStyleModal(true);
  };



  const tableActions = useMemo(() => ({
    handleEditStyle: (style) => {
      setEditingStyle(style);

      // Resolve Main Category / Sub Category
      const subcategory = subCategories.find(sub => parseInt(sub.category_id) === parseInt(style.category_id));
      const mainCategoryId = style.main_category_id || subcategory?.parent_id || '';

      // Resolve Marketplace Category / Sub Category
      const mpMainCategoryId = style.marketplace_main_category_id || '';
      const mpSubCategoryId = style.marketplace_category_id || '';

      setSelectedMainCategory(mainCategoryId.toString());
      setSelectedMarketplaceCategory(mpMainCategoryId ? mpMainCategoryId.toString() : '');
      setSelectedMarketplaceSubcategory(mpSubCategoryId ? mpSubCategoryId.toString() : '');

      // Pre-fetch subcategories for the dropdowns
      if (mainCategoryId) {
        fetch(`${BASE_URL}/api/admin/categories/subcategories/${mainCategoryId}?company_code=${company_code}`)
          .then(res => res.json())
          .then(data => {
            if (data.success) setSubCategories(data.subcategories);
          });
      }
      if (mpMainCategoryId) {
        fetch(`${BASE_URL}/api/admin/categories/marketplace_subcategories/${mpMainCategoryId}`)
          .then(res => res.json())
          .then(data => {
            if (data.success) setMarketplaceSubcategories(data.subcategories || []);
          });
      }

      setStyleForm({
        style_number: style.style_number || '',
        name: style.name,
        description: style.description || '',
        category_id: style.category_id ? style.category_id.toString() : '',
        main_category_id: mainCategoryId.toString(),
        marketplace_category_id: mpSubCategoryId ? mpSubCategoryId.toString() : '', // leaf ID
        marketplace_main_category_id: mpMainCategoryId ? mpMainCategoryId.toString() : '', // parent ID
        images: [],
        company_code: company_code
      });
      setShowStyleModal(true);
    },
    handleDeleteStyle: async (styleId) => {
      // Deletion confirmation is now handled by DeleteModal in StyleTable
      try {
        const response = await fetch(`${BASE_URL}/api/admin/styles/delete-styles/${styleId}`, {
          method: 'DELETE'
        });

        const data = await response.json();

        if (data.success) {
          showSuccess('Product deleted successfully!', 'Success');
          // Clear selected style and close modals if the deleted style was selected
          setSelectedStyle(prev => (prev && prev.style_id === styleId ? null : prev));
          setShowVariantModal(false);
          setEditingStyle(prev => (prev && prev.style_id === styleId ? null : prev));
          setShowStyleModal(false);
          fetchStyles();
        }
        else {
          showError(data.message || 'Failed to delete product', 'Error');
        }
      } catch (err) {
        showError(err.message || 'Error deleting product', 'Error');
      }
    },
    handleManageAttributes: (style) => {
      navigate(`/products/${style.style_number}/attributes`);
    },
    handleToggleIsView: async (style, toView) => {
      // Always allow click, but check requirements and show error/success
      const requiredFields = [
        style.style_number,
        style.name,
        style.description,
        style.category_id,
        style.main_category_id,
        style.image
      ];
      const allFieldsFilled = requiredFields.every(f => f !== null && f !== undefined && f !== '' && f !== 'null');
      if (toView && (!allFieldsFilled || style.approved !== 'yes')) {
        showError('All fields must be filled and approved to publish on client side.', 'Error');
        return;
      }
      try {
        const response = await fetch(`${BASE_URL}/api/admin/styles/update-is-view/${style.style_id}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ is_view: toView ? 'yes' : 'no' })
          }
        );
        const data = await response.json();
        if (data.success) {
          // Fetch styles first, then show success message
          await fetchStyles();
          if (toView) {
            showSuccess('Product Published Successfully!', 'Success');
          }
          else {
            showSuccess('Product Unpublished Successfully!', 'Success');
          }
        } else {
          showError(data.message || 'Failed to update view status', 'Error');
        }
      } catch (err) {
        showError(err.message || 'Error updating view status', 'Error');
      }
    },
    handleMeasureGuide: () => {
      setShowMeasureGuideModal(true);
    },
    handleToggleApproval: async (style, toApprove) => {
      try {
        const response = await fetch(`${BASE_URL}/api/admin/styles/update-approval/${style.style_id}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ approved: toApprove ? 'yes' : 'no' })
          }
        );
        const data = await response.json();
        if (data.success) {
          showSuccess(`Style ${toApprove ? 'approved' : 'unapproved'} successfully!`, 'Success');
          fetchStyles();
        } else {
          showError(data.message || 'Failed to update approval status', 'Error');
        }
      } catch (err) {
        showError(err.message || 'Error updating approval status', 'Error');
      }
    },
    handleTogglePublishCommon: async (style, toPublish) => {
      // Always allow click, but check requirements and show error/success]
      const requiredFields = [
        style.style_number,
        style.name,
        style.description,
        style.category_id,
        style.main_category_id,
        style.image
      ];
      const allFieldsFilled = requiredFields.every(f => f !== null && f !== undefined && f !== '' && f !== 'null');
      if (toPublish && (!allFieldsFilled || style.approved !== 'yes')) {
        showError('All fields must be filled and approved to publish on common website.', 'Error');
        return;
      }
      try {
        const response = await fetch(`${BASE_URL}/api/admin/styles/update-publish-common/${style.style_id}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ publish_common: toPublish ? 'yes' : 'no' })
          }
        );
        const data = await response.json();
        if (data.success) {
          await fetchStyles();
          if (toPublish) {
            showSuccess('Product published on common website successfully!', 'Success');
          } else {
            showSuccess('Product unpublished from common website successfully!', 'Success');
          }
        } else {
          showError(data.message || 'Failed to update publish common status', 'Error');
        }
      } catch (err) {
        showError(err.message || 'Error updating publish common status', 'Error');
      }
    },
    getIsViewError: () => '', // No longer used, but keep for compatibility
    getPublishCommonError: () => '' // For consistency with other error handlers
  }), [company_code, subCategories, fetchStyles, navigate, showError, showSuccess]);

  const handleSaveStyle = async () => {
    setLoading(true);

    // Check required fields
    const requiredFields = [
      styleForm.style_number,
      styleForm.name,
      styleForm.description,
      styleForm.category_id,
      styleForm.main_category_id,
      styleForm.images && styleForm.images.length > 0
    ];
    const allFieldsFilled = requiredFields.every(f => f !== null && f !== undefined && f !== '' && f !== false && f !== 'null');

    // Set is_view based on required fields
    const formData = new FormData();
    Object.keys(styleForm).forEach(key => {
      if (key === 'images') {
        styleForm.images.forEach(image => {
          formData.append('images', image);
        });
      } else if (key === 'style_number') {
        formData.append('style_number', styleForm.style_number); // send as style_number
      } else if (key === 'is_view') {
        // skip, will set below
      } else {
        formData.append(key, styleForm[key]);
      }
    });
    // For new styles, always set is_view to 'no'
    // For editing, set based on required fields
    formData.append('is_view', editingStyle ? (allFieldsFilled ? 'yes' : 'no') : 'no');

    try {
      const url = editingStyle
        ? `${BASE_URL}/api/admin/styles/update-styles/${editingStyle.style_id}`
        : `${BASE_URL}/api/admin/styles/add-styles`;
      const method = editingStyle ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        showSuccess(editingStyle ? ' updated successfully!' : ' added successfully!', 'Success');
        setShowStyleModal(false);
        fetchStyles();
      } else {
        showError(data.message || 'Failed to save style', 'Error');
      }
    } catch (err) {
      showError(err.message || 'Error saving style', 'Error');
    }
    setLoading(false);
  };

  const handleSaveVariant = async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/admin/styles/add-style-variants`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...variantForm,
          company_code,
          style_number: selectedStyle.style_number
        })
      });

      const data = await response.json();
      if (data.success) {
        showSuccess('Variant added successfully', 'Success');
        fetchVariants(selectedStyle.style_number);
        setVariantForm({
          color_id: '',
          size_id: '',
          fit_id: '',
          material_id: '',
          unit_price: '',
          sale_price: '',
        });
      } else {
        showError(data.message || 'Failed to add variant', 'Error');
      }
    } catch (err) {
      showError(err.message || 'Error saving variant', 'Error');
    }
  };

  const handleEditVariant = async (variantId) => {
    try {
      const response = await fetch(`${BASE_URL}/api/admin/styles/update-style-variants/${variantId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...variantForm,
          company_code
        })
      });

      const data = await response.json();
      if (data.success) {
        showSuccess('Variant updated successfully', 'Success');
        fetchVariants(selectedStyle.style_number);
        setVariantForm({
          color_id: '',
          size_id: '',
          fit_id: '',
          material_id: '',
          unit_price: '',
          sale_price: '',
        });
        setIsEditingVariant(false);
        setEditingVariantId(null);
      } else {
        showError(data.message || 'Failed to update variant', 'Error');
      }
    } catch (err) {
      showError(err.message || 'Error updating variant', 'Error');
    }
  };

  const handleDeleteVariant = async (variantId) => {
    // Deletion confirmation is now handled by DeleteModal in StyleTable (if implemented for variants)
    try {
      const response = await fetch(`${BASE_URL}/api/admin/styles/delete-style-variants/${variantId}`, {
        method: 'DELETE'
      });

      const data = await response.json();
      if (data.success) {
        showSuccess('Variant deleted successfully', 'Success');
        fetchVariants(selectedStyle.style_number);
      } else {
        showError(data.message || 'Failed to delete variant', 'Error');
      }
    } catch (err) {
      showError(err.message || 'Error deleting variant', 'Error');
    }
  };

  const handleMainCategoryChange = async (categoryId) => {
    setSelectedMainCategory(categoryId);
    setStyleForm(prev => ({
      ...prev,
      category_id: '',
      main_category_id: categoryId
    }));

    if (categoryId) {
      try {
        const response = await fetch(`${BASE_URL}/api/admin/categories/subcategories/${categoryId}?company_code=${company_code}`);
        const data = await response.json();
        if (data.success) {
          setSubCategories(data.subcategories);
        }
      } catch (err) {
        console.error('Error fetching subcategories:', err);
      }
    } else {
      setSubCategories([]);
    }
  };

  const handleMarketplaceCategoryChange = (categoryId) => {
    setSelectedMarketplaceCategory(categoryId);
    setStyleForm(prev => ({
      ...prev,
      marketplace_main_category_id: categoryId,
      marketplace_category_id: '' // clear subcategory until selected
    }));
    // Fetch subcategories for the selected marketplace category
    fetchMarketplaceSubcategories(categoryId);
  };

  const fetchMarketplaceSubcategories = async (parentId) => {
    try {
      const response = await fetch(`${BASE_URL}/api/admin/categories/marketplace_subcategories/${parentId}`);
      const data = await response.json();
      if (data.success) {
        setMarketplaceSubcategories(data.subcategories || []);
      }
    } catch (err) {
      console.error('Error fetching marketplace subcategories:', err);
      setMarketplaceSubcategories([]);
    }
  };

  const getMarketplaceSubcategoriesForCategory = (categoryId) => {
    return marketplaceSubcategories;
  };

  const getSubcategoriesForMainCategory = () => {
    return subCategories;
  };

  // Add row click handler
  const handleRowClick = useCallback((style) => {
    setSelectedStyle(style);
    fetchVariants(style.style_number);
    setShowVariantModal(true);
  }, [fetchVariants]);

  return (
    <div className="style-management">
      {/* Header */}
      <div className="style-header">
        <h1>Style Management</h1>
        <div>
          <button className="add-style-btn" onClick={handleAddStyle}>
            <FaPlus size={14} />
            Add New Product
          </button>
        </div>
      </div>

      {/* Styles Table */}
      <div className="styles-table-container">
        {loading ? (
          <Spinner text="Loading products..." />
        ) : styles.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-content">
              <div className="empty-state-icon">
                <i className="bi bi-diagram-3"></i>
              </div>
              <h3>No Products found</h3>
              <p> Click the <b>Add New Product</b> button to create your first product! </p>
            </div>
          </div>
        ) : (
          <StyleTable
            styles={styles}
            loading={loading}
            tableActions={tableActions}
            handleRowClick={handleRowClick}
            BASE_URL={BASE_URL}
            onAfterDelete={fetchStyles}
            userRole={userData?.role}
          />
        )}
      </div>

      <StyleFormModal
        show={showStyleModal}
        onHide={() => setShowStyleModal(false)}
        editingStyle={editingStyle}
        styleForm={styleForm}
        setStyleForm={setStyleForm}
        selectedMainCategory={selectedMainCategory}
        handleMainCategoryChange={handleMainCategoryChange}
        mainCategories={mainCategories}
        selectedMarketplaceCategory={selectedMarketplaceCategory}
        handleMarketplaceCategoryChange={handleMarketplaceCategoryChange}
        marketplaceCategories={marketplaceCategories}
        marketplaceSubcategories={marketplaceSubcategories}
        getMarketplaceSubcategoriesForCategory={
          getMarketplaceSubcategoriesForCategory
        }
        getSubcategoriesForMainCategory={getSubcategoriesForMainCategory}
        handleSaveStyle={handleSaveStyle}
        loading={loading}
        BASE_URL={BASE_URL}
      />

      <VariantFormModal
        show={showVariantModal}
        onHide={() => setShowVariantModal(false)}
        selectedStyle={selectedStyle}
        variantForm={variantForm}
        setVariantForm={setVariantForm}
        handleSaveVariant={handleSaveVariant}
        handleEditVariant={handleEditVariant}
        handleDeleteVariant={handleDeleteVariant}
        variants={variants}
        colors={colors}
        sizes={sizes}
        materials={materials}
        fits={fits}
        isEditing={isEditingVariant}
        editingId={editingVariantId}
        setIsEditing={setIsEditingVariant}
        setEditingId={setEditingVariantId}
        companyCurrency={companyCurrency}
      />

      <MeasureGuideModal
        show={showMeasureGuideModal}
        onHide={() => setShowMeasureGuideModal(false)}
        companyCode={company_code}
        onSave={(data) => {
          showSuccess("Measure guide added successfully!", "Success");
        }}
      />

      {/* Success Modal */}
      <SuccessModal
        show={showSuccessModal}
        onHide={hideSuccess}
        message={successMessage}
        title={successTitle}
        variant="success"
      />

      {/* Error Modal */}
      <SuccessModal
        show={showErrorModal}
        onHide={hideError}
        message={errorMessage}
        title={errorTitle}
        variant="error"
      />
    </div>
  );
}